import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Shield, UserX } from 'lucide-react'
import type { User } from '@/types/entities'

interface UserDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    user: User | null
    onConfirm: () => void
    isLoading?: boolean
}

export function UserDeleteDialog({
    open,
    onOpenChange,
    user,
    onConfirm,
    isLoading = false,
}: UserDeleteDialogProps) {
    const { t } = useTranslation()
    if (!user) return null;

    // Check if user is an admin
    const isAdmin = user.roles?.some(role => role.name === 'admin');
    const isSystemUser = user.is_system;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserX className="h-5 w-5 text-destructive" />
                        {t('users.deleteUserTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('users.confirmDelete')} "{user.name}"? {t('users.deleteWarning')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Admin Warning */}
                    {isAdmin && (
                        <Alert variant="destructive">
                            <Shield className="h-4 w-4" />
                            <AlertDescription>
                                {t('userForm.adminDeleteWarning')}
                                {isSystemUser && (
                                    <><br /><br />
                                        {t('userForm.systemUserWarning')}
                                    </>
                                )}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* General Warning */}
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('userForm.permanentAction')}
                        </AlertDescription>
                    </Alert>

                    {/* User Details */}
                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div><strong>{t('userForm.name')}:</strong> {user.name}</div>
                            <div><strong>{t('userForm.email')}:</strong> {user.email}</div>
                            <div><strong>{t('users.roles')}:</strong> {user.roles?.map(role => i18n.exists(`roles.${role.name}`) ? t(`roles.${role.name}`) : role.name).join(', ') || t('common.notAvailable')}</div>
                            <div><strong>{t('users.status')}:</strong> {user.isActive ? t('users.active') : t('users.inactive')}</div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('userForm.deleting') : t('users.deleteUser')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}



